import React, { useEffect, useState } from "react";
import api, { API, formatIDR } from "../lib/api";
import Layout from "../components/Layout";
import { Download } from "lucide-react";

const STATUS_COLORS = {
  Dijadwalkan: "bg-[#2563EB]/10 text-[#2563EB] border-[#2563EB]/20",
  Berjalan: "bg-[#D97706]/10 text-[#D97706] border-[#D97706]/20",
  Selesai: "bg-[#059669]/10 text-[#059669] border-[#059669]/20",
  Dibatalkan: "bg-[#DC2626]/10 text-[#DC2626] border-[#DC2626]/20",
};

const isoDate = (d) => d.toISOString().slice(0, 10);
const presetRange = (preset) => {
  const today = new Date();
  if (preset === "this-week") {
    const day = today.getDay() || 7;
    const monday = new Date(today); monday.setDate(today.getDate() - day + 1);
    const sunday = new Date(monday); sunday.setDate(monday.getDate() + 6);
    return { start: isoDate(monday), end: isoDate(sunday) };
  }
  if (preset === "this-month") {
    const first = new Date(today.getFullYear(), today.getMonth(), 1);
    const last = new Date(today.getFullYear(), today.getMonth() + 1, 0);
    return { start: isoDate(first), end: isoDate(last) };
  }
  if (preset === "last-month") {
    const first = new Date(today.getFullYear(), today.getMonth() - 1, 1);
    const last = new Date(today.getFullYear(), today.getMonth(), 0);
    return { start: isoDate(first), end: isoDate(last) };
  }
  return { start: "", end: "" };
};

const Reports = () => {
  const [tab, setTab] = useState("customer");
  const [customers, setCustomers] = useState([]);
  const [technicians, setTechnicians] = useState([]);
  const [selCust, setSelCust] = useState("");
  const [selTech, setSelTech] = useState("");
  const [custReport, setCustReport] = useState(null);
  const [techReport, setTechReport] = useState(null);
  const [techStart, setTechStart] = useState("");
  const [techEnd, setTechEnd] = useState("");
  const [techPreset, setTechPreset] = useState("all");

  const applyTechPreset = (p) => {
    setTechPreset(p);
    if (p === "all" || p === "custom") { return; }
    const { start, end } = presetRange(p);
    setTechStart(start); setTechEnd(end);
  };

  const downloadTechXlsx = () => {
    const token = localStorage.getItem("om_token");
    const params = new URLSearchParams();
    if (techStart) params.set("start", techStart);
    if (techEnd) params.set("end", techEnd);
    if (token) params.set("token", token);
    const url = `${API}/reports/technicians.xlsx?${params.toString()}`;
    window.open(url, "_blank");
  };

  useEffect(() => {
    api.get("/customers").then(r => setCustomers(r.data));
    api.get("/technicians").then(r => setTechnicians(r.data));
  }, []);

  useEffect(() => {
    if (!selCust) { setCustReport(null); return; }
    api.get(`/reports/customer/${selCust}`).then(r => setCustReport(r.data));
  }, [selCust]);

  useEffect(() => {
    if (!selTech) { setTechReport(null); return; }
    api.get(`/reports/technician/${selTech}`).then(r => setTechReport(r.data));
  }, [selTech]);

  return (
    <Layout>
      <div className="mb-8">
        <div className="label-cap mb-2">Analitik</div>
        <h1 className="font-heading text-4xl font-black tracking-tight">Laporan</h1>
        <p className="text-xs text-[#52525B] mt-2">Laporan Keuangan telah dipindahkan ke <span className="font-bold">Pengaturan → Laporan Keuangan</span>.</p>
      </div>

      <div className="flex gap-2 mb-6">
        <button onClick={() => setTab("customer")} data-testid="tab-report-customer" className={`px-4 py-2 text-xs font-bold uppercase tracking-wider border ${tab === "customer" ? "bg-[#111113] text-white border-[#111113]" : "border-[#E4E4E7] bg-white"}`}>Per Pelanggan</button>
        <button onClick={() => setTab("technician")} data-testid="tab-report-technician" className={`px-4 py-2 text-xs font-bold uppercase tracking-wider border ${tab === "technician" ? "bg-[#111113] text-white border-[#111113]" : "border-[#E4E4E7] bg-white"}`}>Per Teknisi</button>
        <button onClick={() => setTab("orders")} data-testid="tab-report-orders" className={`px-4 py-2 text-xs font-bold uppercase tracking-wider border ${tab === "orders" ? "bg-[#111113] text-white border-[#111113]" : "border-[#E4E4E7] bg-white"}`}>Export Order</button>
      </div>

      {tab === "customer" && (
        <div>
          <select data-testid="report-customer-select" value={selCust} onChange={(e) => setSelCust(e.target.value)} className="px-3 py-2 border border-[#E4E4E7] text-sm bg-white outline-none focus:border-[#002FA7] mb-6 w-full max-w-md">
            <option value="">— Pilih pelanggan —</option>
            {customers.map(c => <option key={c.id} value={c.id}>{c.name} · {c.phone}</option>)}
          </select>
          {custReport && (
            <div data-testid="customer-report">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-px bg-[#E4E4E7] border border-[#E4E4E7] mb-6">
                <Stat label="Pelanggan" value={custReport.customer.name} />
                <Stat label="Total Pekerjaan" value={custReport.total_jobs} mono />
                <Stat label="Total Belanja (Lunas)" value={formatIDR(custReport.total_spent)} mono />
              </div>
              <OrderTable orders={custReport.orders} />
            </div>
          )}
        </div>
      )}

      {tab === "technician" && (
        <div>
          <div className="flex items-end gap-3 flex-wrap mb-4">
            <div className="flex-1 min-w-[260px]">
              <label className="label-cap block mb-1">Teknisi</label>
              <select data-testid="report-technician-select" value={selTech} onChange={(e) => setSelTech(e.target.value)} className="px-3 py-2 border border-[#E4E4E7] text-sm bg-white outline-none focus:border-[#002FA7] w-full">
                <option value="">— Pilih teknisi (atau export semua) —</option>
                {technicians.map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
              </select>
            </div>
            <div>
              <label className="label-cap block mb-1">Periode</label>
              <select value={techPreset} onChange={(e) => applyTechPreset(e.target.value)} data-testid="tech-period-preset" className="px-3 py-2 border border-[#E4E4E7] text-sm bg-white">
                <option value="all">Semua</option>
                <option value="this-week">Minggu Ini</option>
                <option value="this-month">Bulan Ini</option>
                <option value="last-month">Bulan Lalu</option>
                <option value="custom">Custom</option>
              </select>
            </div>
            <div>
              <label className="label-cap block mb-1">Dari</label>
              <input type="date" value={techStart} onChange={(e) => { setTechStart(e.target.value); setTechPreset("custom"); }} className="px-3 py-2 border border-[#E4E4E7] text-sm font-mono bg-white" data-testid="tech-start-input" />
            </div>
            <div>
              <label className="label-cap block mb-1">Sampai</label>
              <input type="date" value={techEnd} onChange={(e) => { setTechEnd(e.target.value); setTechPreset("custom"); }} className="px-3 py-2 border border-[#E4E4E7] text-sm font-mono bg-white" data-testid="tech-end-input" />
            </div>
            <button onClick={downloadTechXlsx} data-testid="download-tech-xlsx-button" className="bg-[#059669] hover:bg-[#047857] text-white text-xs font-bold uppercase tracking-wider px-4 py-2.5 flex items-center gap-2">
              <Download size={14} /> Download Excel
            </button>
          </div>
          {techReport && (() => {
            const filtered = (techReport.orders || []).filter((o) => {
              if (techStart && (o.schedule_date || "") < techStart) return false;
              if (techEnd && (o.schedule_date || "") > techEnd) return false;
              return true;
            });
            const completedF = filtered.filter((o) => o.work_status === "Selesai");
            const techCount = (o) => {
              const ids = new Set((o.assignments || []).map((a) => a.technician_id).filter(Boolean));
              if (o.technician_id) ids.add(o.technician_id);
              return Math.max(ids.size, 1);
            };
            const revenueF = completedF.reduce((s, o) => s + (o.total || 0) / techCount(o), 0);
            const bonusF = completedF.reduce((s, o) => s + (o.items || []).reduce((a, it) => a + (Number(it.point || 0) * Number(it.qty || 0)), 0), 0);
            return (
              <div data-testid="technician-report">
                <div className="grid grid-cols-2 md:grid-cols-5 gap-px bg-[#E4E4E7] border border-[#E4E4E7] mb-6">
                  <Stat label="Teknisi" value={techReport.technician.name} />
                  <Stat label="Total Pekerjaan" value={filtered.length} mono />
                  <Stat label="Selesai" value={completedF.length} mono />
                  <Stat label="Total Nilai" value={formatIDR(revenueF)} mono />
                  <Stat label="Bonus Point" value={<span className="text-[#7C3AED]">{bonusF}</span>} mono />
                </div>
                <OrderTable orders={filtered} />
              </div>
            );
          })()}
        </div>
      )}

      {tab === "orders" && (
        <div data-testid="orders-export-tab">
          <div className="flex items-end gap-3 flex-wrap mb-4">
            <div>
              <label className="label-cap block mb-1">Periode</label>
              <select
                value={techPreset}
                onChange={(e) => applyTechPreset(e.target.value)}
                data-testid="orders-period-preset"
                className="px-3 py-2 border border-[#E4E4E7] text-sm bg-white"
              >
                <option value="all">Semua</option>
                <option value="this-week">Minggu Ini</option>
                <option value="this-month">Bulan Ini</option>
                <option value="last-month">Bulan Lalu</option>
                <option value="custom">Custom</option>
              </select>
            </div>
            <div>
              <label className="label-cap block mb-1">Dari</label>
              <input type="date" value={techStart} onChange={(e) => { setTechStart(e.target.value); setTechPreset("custom"); }} className="px-3 py-2 border border-[#E4E4E7] text-sm font-mono bg-white" data-testid="orders-start-input" />
            </div>
            <div>
              <label className="label-cap block mb-1">Sampai</label>
              <input type="date" value={techEnd} onChange={(e) => { setTechEnd(e.target.value); setTechPreset("custom"); }} className="px-3 py-2 border border-[#E4E4E7] text-sm font-mono bg-white" data-testid="orders-end-input" />
            </div>
            <button
              onClick={() => {
                const token = localStorage.getItem("om_token");
                const params = new URLSearchParams({ start: techStart, end: techEnd, token: token || "" });
                window.open(`${API}/reports/orders.xlsx?${params.toString()}`, "_blank");
              }}
              data-testid="download-orders-xlsx-button"
              className="bg-[#059669] hover:bg-[#047857] text-white text-xs font-bold uppercase tracking-wider px-4 py-2.5 flex items-center gap-2"
            >
              <Download size={14} /> Download Excel
            </button>
          </div>
          <div className="bg-white border border-[#E4E4E7] p-6">
            <div className="label-cap mb-2">Kolom yang di-export</div>
            <div className="text-sm text-[#52525B] space-y-1">
              <div>1. Tanggal Pengerjaan</div>
              <div>2. No Order</div>
              <div>3. Nama Client</div>
              <div>4. Alamat</div>
              <div>5. Kota</div>
              <div>6. No Telp</div>
              <div>7. Status Pekerjaan</div>
              <div>8. Teknisi (bisa lebih dari satu, dipisah koma)</div>
              <div>9. Total Tagihan</div>
              <div>10. Pembayaran (Tunai / Transfer · Nama Bank / QRIS / dll)</div>
            </div>
            <div className="mt-4 text-[10px] text-[#52525B]">
              Range default: <span className="font-mono">{techStart || "awal"}</span> s/d <span className="font-mono">{techEnd || "sekarang"}</span>. Pilih preset atau isi tanggal custom, lalu klik tombol Download Excel.
            </div>
          </div>
        </div>
      )}
    </Layout>
  );
};

const Stat = ({ label, value, mono }) => (
  <div className="bg-white p-6">
    <div className="label-cap mb-2">{label}</div>
    <div className={`font-heading font-black tracking-tight ${mono ? "text-3xl font-mono" : "text-xl"}`}>{value}</div>
  </div>
);

const OrderTable = ({ orders }) => (
  <div className="bg-white border border-[#E4E4E7]">
    <table className="w-full">
      <thead>
        <tr className="border-b border-[#E4E4E7]">
          <th className="text-left label-cap px-4 py-3">No</th>
          <th className="text-left label-cap px-4 py-3">Jadwal</th>
          <th className="text-left label-cap px-4 py-3">Item</th>
          <th className="text-left label-cap px-4 py-3">Status</th>
          <th className="text-left label-cap px-4 py-3">Bayar</th>
          <th className="text-right label-cap px-4 py-3">Total</th>
        </tr>
      </thead>
      <tbody>
        {orders.length === 0 && <tr><td colSpan="6" className="p-6 text-center text-sm text-[#52525B]">Belum ada riwayat.</td></tr>}
        {orders.map(o => (
          <tr key={o.id} className="border-b border-[#E4E4E7]">
            <td className="px-4 py-3 text-xs font-mono">{o.order_no}</td>
            <td className="px-4 py-3 text-xs font-mono">{o.schedule_date}</td>
            <td className="px-4 py-3 text-sm text-[#52525B] max-w-md truncate">{(o.items || []).map(i => i.description).join(", ")}</td>
            <td className="px-4 py-3"><span className={`text-xs font-bold px-2 py-1 border ${STATUS_COLORS[o.work_status]}`}>{o.work_status}</span></td>
            <td className="px-4 py-3 text-xs">{o.payment_status}</td>
            <td className="px-4 py-3 text-sm font-mono font-bold text-right">{formatIDR(o.total)}</td>
          </tr>
        ))}
      </tbody>
    </table>
  </div>
);

export default Reports;
